<main>
  <loading-overlay [isLoading]="isLoading()" />

  <section class="content">
    <div class="page__title">
      <div class="page__title_container">
        <h1>{{ 'dashboardProductionCards.pageTitle' | translateWF: 'Production Cards Dashboard' }}</h1>
      </div>
    </div>

    <!-- Filter Section -->
    <p-panel header="Filter">
      <ng-template pTemplate="icons">
        <p-button
          [label]="'dashboardProductionCards.clear' | translateWF: 'Clear'"
          icon="pi pi-times"
          severity="secondary"
          [outlined]="true"
          (onClick)="clearFilters(); applyFilters()"
        />
        <p-button [label]="'Apply' | translateWF: 'Apply'" icon="pi pi-search" (onClick)="applyFilters()" />
      </ng-template>
      <p-card>
        <div class="grid-auto-4">
          <div class="form-box">
            <label for="filterWorkOrder">{{
              'dashboardProductionCards.filterWorkOrderNumber'
                | translateWF
                  : 'Work
              Order Number'
            }}</label>
            <input
              id="filterWorkOrder"
              type="text"
              pInputText
              [formControl]="filterWorkOrderNumber"
              [placeholder]="'dashboardProductionCards.searchByWorkOrder' | translateWF: 'Search by Work Order...'"
              class="w-full"
              (keydown.enter)="applyFilters()"
            />
          </div>
          <div class="form-box">
            <label for="filterProductionCard">{{ 'dashboardProductionCards.filterProductionCardNumber' | translateWF: 'Production Card Number' }}</label>
            <input
              id="filterProductionCard"
              type="text"
              pInputText
              [formControl]="filterProductionCardNumber"
              [placeholder]="'dashboardProductionCards.searchByPCNumber' | translateWF: 'Search by PC Number...'"
              class="w-full"
              (keydown.enter)="applyFilters()"
            />
          </div>
          <div class="form-box">
            <label for="filterWOStatus">{{
              'dashboardProductionCards.filterWorkOrderStatus'
                | translateWF
                  : 'Work Order
              Status'
            }}</label>
            <p-multiselect
              id="filterWOStatus"
              [options]="workOrderStatusOptions"
              [formControl]="filterWorkOrderStatus"
              optionLabel="label"
              optionValue="value"
              [placeholder]="'dashboardProductionCards.selectWOStatus' | translateWF: 'Select WO Status'"
              [maxSelectedLabels]="2"
              class="w-full"
            />
          </div>
          <div class="form-box">
            <label for="filterPCStatus">{{ 'dashboardProductionCards.filterProductionCardStatus' | translateWF: 'Production Card Status' }}</label>
            <p-multiselect
              id="filterPCStatus"
              [options]="productionCardStatusOptions"
              [formControl]="filterProductionCardStatus"
              optionLabel="label"
              optionValue="value"
              [placeholder]="'dashboardProductionCards.selectPCStatus' | translateWF: 'Select PC Status'"
              [maxSelectedLabels]="2"
              class="w-full"
            />
          </div>
        </div>
      </p-card>
    </p-panel>

    <p-panel header="Results">
      <p-card data-variant="compact">
        <p-treetable
          [value]="treeData()"
          [columns]="columns()"
          [scrollable]="true"
          [paginator]="true"
          [rows]="pageSize()"
          [first]="firstRecord()"
          [totalRecords]="totalCount()"
          [lazy]="true"
          (onLazyLoad)="onPageChange($any($event))"
          [rowsPerPageOptions]="[5, 10, 25, 50]"
          [rowHover]="true"
          styleClass="cs-treetable-hierarchy"
        >
          <ng-template pTemplate="header" let-columns>
            <tr>
              @for (col of columns; track $index) {
                <th
                  [class.text-left]="col.align === 'left'"
                  [class.text-right]="col.align === 'right'"
                  [class.text-center]="col.align === 'center'"
                  [style.width]="col.width"
                  [style.max-width]="col.width"
                >
                  {{ col.header | translateWF: col.headerFallback }}
                </th>
              }
            </tr>
          </ng-template>

          <ng-template pTemplate="body" let-rowNode let-rowData="rowData" let-columns="columns">
            <tr [ttRow]="rowNode" [class]="'row-type-' + rowData.type?.toLowerCase()" [class.row-expanded]="rowNode.node.expanded">
              @for (col of columns; track $index) {
                <td
                  [class.text-left]="col.align === 'left'"
                  [class.text-right]="col.align === 'right'"
                  [class.text-center]="col.align === 'center'"
                  [style.width]="col.width"
                  [style.max-width]="col.width"
                >
                  @if (col.field === 'toggler') {
                    <p-treeTableToggler [rowNode]="rowNode"></p-treeTableToggler>
                  } @else if (col.field === 'orderNumber') {
                    @if (rowData.type === 'WORK_ORDER') {
                      <div class="flex items-center gap-2">
                        <a [href]="'/production-cards-details?id=' + rowData.id + '&workOrderId=' + rowData.id" target="_blank" class="order-link">
                          <strong>{{ rowData.workOrderNumber }}</strong>
                        </a>
                        <p-button
                          icon="pi pi-info-circle"
                          [rounded]="true"
                          [text]="true"
                          severity="info"
                          size="small"
                          [pTooltip]="'dashboardProductionCards.viewQuantities' | translateWF: 'View quantities'"
                          (onClick)="openWorkOrderQuantitiesModal(rowData.originalWorkOrder)"
                        />
                      </div>
                      <div class="text-sm text-gray-500">{{ getWorkOrderTypeLabel(rowData.workOrderType) }} | {{ getWorkOrderTermLabel(rowData.workOrderTerm) }}</div>
                    } @else if (rowData.type === 'PRODUCTION_CARD') {
                      <div class="flex gap-2 items-center">
                        <p-button
                          variant="text"
                          [outlined]="true"
                          icon="pi pi-print"
                          size="small"
                          (onClick)="printProductionCardReport(rowData.productionCardId!, rowData.productionCardNumber!)"
                        />
                        <a [href]="'/production-card/' + rowData.productionCardNumber + '/details'" target="_blank" class="order-link">
                          <strong>{{ rowData.productionCardNumber }}</strong>
                        </a>
                      </div>
                    } @else if (rowData.type === 'FABRIC') {
                      <div class="fabric-info">
                        <span class="fabric-code">{{ rowData.fabricCode }}</span>
                        <span class="fabric-desc">{{ rowData.fabricDescription }}</span>
                      </div>
                    }
                  } @else if (col.field === 'status') {
                    @if (rowData.type === 'WORK_ORDER' || rowData.type === 'PRODUCTION_CARD') {
                      <span [class]="'status-badge status-' + getStatusClass(rowData.status, rowData.type)">
                        {{ getStatusLabel(rowData.status, rowData.type) }}
                      </span>
                    } @else if (rowData.type === 'FABRIC') {
                      @if (rowData.finishingRouteNumber) {
                        <div class="finishing-route">
                          <span class="route-number">FR: {{ rowData.finishingRouteNumber }}</span>
                          @if (rowData.processProgress) {
                            <span class="process-progress">{{ rowData.processProgress }} {{ 'dashboardProductionCards.processes' | translateWF: 'processes' }}</span>
                          }
                        </div>
                      }
                    }
                  } @else if (col.field === 'clientInfo') {
                    @if (rowData.type === 'WORK_ORDER') {
                      <div class="client-info">
                        <span class="client-name">{{ rowData.clientName }}</span>
                        <span class="client-code text-sm text-gray-500">&nbsp; ({{ rowData.clientCode }})</span>
                      </div>
                    } @else if (rowData.type === 'PRODUCTION_CARD') {
                      <span class="color-name" [title]="rowData.colorReference">{{ rowData.colorName || rowData.colorReference }}</span>
                    } @else if (rowData.type === 'FABRIC') {
                      @if (rowData.lastProcessName) {
                        <span class="last-process">{{ rowData.lastProcessName }}</span>
                      }
                    }
                  } @else if (col.field === 'brandInfo') {
                    @if (rowData.type === 'WORK_ORDER') {
                      <div class="brand-info">
                        <span class="brand-name">{{ rowData.brandName }}</span>
                        <span class="brand-code text-sm text-gray-500">&nbsp; ({{ rowData.brandCode }})</span>
                      </div>
                    } @else if (rowData.type === 'PRODUCTION_CARD') {
                      <span class="labdip-number">{{ rowData.labDipNumber || '—' }}</span>
                    }
                  } @else if (col.field === 'requiredQty') {
                    @if (rowData.type === 'WORK_ORDER') {
                      <div class="qty-cell">{{ rowData.requiredQuantityDisplay }}</div>
                    } @else if (rowData.type === 'PRODUCTION_CARD') {
                      <div class="qty-cell">{{ rowData.plannedQuantityDisplay }}</div>
                    } @else if (rowData.type === 'FABRIC') {
                      <div class="qty-cell">{{ rowData.plannedQuantityDisplay }}</div>
                    }
                  } @else if (col.field === 'knittedQty') {
                    @if (rowData.type === 'WORK_ORDER') {
                      <div class="qty-cell qty-multiline">{{ rowData.knittedQuantityDisplay }}</div>
                    }
                  } @else if (col.field === 'batchedQty') {
                    @if (rowData.type === 'WORK_ORDER') {
                      <div class="qty-cell qty-multiline">{{ rowData.batchedQuantityDisplay }}</div>
                    }
                  } @else if (col.field === 'dyedQty') {
                    @if (rowData.type === 'WORK_ORDER') {
                      <div class="qty-cell qty-multiline">{{ rowData.dyedQuantityDisplay }}</div>
                    } @else if (rowData.type === 'PRODUCTION_CARD') {
                      <div class="cycle-info">
                        @if (rowData.latestCycleStatus) {
                          <span class="cycle-badge">{{ rowData.latestCycleStatus }}</span>
                        }
                        @if (rowData.latestCycleQCStatus) {
                          <span class="qc-badge">{{ rowData.latestCycleQCStatus }}</span>
                        }
                      </div>
                    }
                  } @else if (col.field === 'weighedQty') {
                    @if (rowData.type === 'WORK_ORDER') {
                      <div class="qty-cell qty-multiline">{{ rowData.weighedQuantityDisplay }}</div>
                    } @else if (rowData.type === 'FABRIC') {
                      <div class="qty-cell">
                        {{ rowData.weighedQuantityDisplay }}
                        @if (rowData.weighedRollsCount) {
                          <div class="text-sm text-gray-500">{{ rowData.weighedRollsCount }} {{ 'dashboardProductionCards.rolls' | translateWF: 'rolls' }}</div>
                        }
                      </div>
                    }
                  } @else if (col.field === 'finishedGoodsQty') {
                    @if (rowData.type === 'WORK_ORDER') {
                      <div class="qty-cell qty-multiline">{{ rowData.finishedGoodsQuantityDisplay }}</div>
                    } @else if (rowData.type === 'PRODUCTION_CARD') {
                      <div class="qty-cell">{{ rowData.actualQuantityDisplay }}</div>
                    } @else if (rowData.type === 'FABRIC') {
                      <div class="qty-cell">{{ rowData.actualQuantityDisplay }}</div>
                    }
                  } @else if (col.field === 'dispatchInfo') {
                    @if (rowData.type === 'WORK_ORDER') {
                      <div class="dispatch-info">
                        <div class="pending">
                          <span class="label">{{ 'dashboardProductionCards.pending' | translateWF: 'Pending' }}:</span>
                          <span class="value">{{ rowData.pendingDispatchQuantityDisplay }}</span>
                        </div>
                        <div class="dispatched">
                          <span class="label">{{ 'dashboardProductionCards.dispatched' | translateWF: 'Dispatched' }}:</span>
                          <span class="value">{{ rowData.dispatchedQuantityDisplay }}</span>
                        </div>
                      </div>
                    }
                  }
                </td>
              }
            </tr>
          </ng-template>

          <ng-template pTemplate="emptymessage">
            <tr>
              <td [attr.colspan]="columns().length" class="text-center p-4">
                <div class="empty-state">
                  <i class="pi pi-inbox text-4xl text-gray-300 mb-2"></i>
                  <p class="text-gray-500">
                    {{
                      'dashboardProductionCards.emptyMessage'
                        | translateWF
                          : 'No work orders
                    found. Try adjusting your filters.'
                    }}
                  </p>
                </div>
              </td>
            </tr>
          </ng-template>
        </p-treetable>
      </p-card>
    </p-panel>
  </section>
</main>
